// 导入 PDF 专用 Promise.withResolvers polyfill - 必须在 react-pdf 之前加载
import "./polyfill";

// 强制在 pdfjs 初始化前再次确保 polyfill 可用
(() => {
  const ensurePolyfill = () => {
    const globalObj = globalThis || window || global || self;
    if (!globalObj.Promise?.withResolvers) {
      const polyfill = function () {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
          resolve = res;
          reject = rej;
        });
        return { promise, resolve, reject };
      };

      try {
        globalObj.Promise.withResolvers = polyfill;
        console.log("[PDF Component] Emergency polyfill injected before pdfjs");
      } catch (e) {
        console.error("[PDF Component] Emergency polyfill injection failed:", e);
      }
    }
  };

  ensurePolyfill();

  // 也为 worker 环境准备
  if (typeof self !== "undefined" && self !== globalThis) {
    try {
      if (!self.Promise?.withResolvers) {
        const polyfill = function () {
          let resolve, reject;
          const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
          });
          return { promise, resolve, reject };
        };
        self.Promise.withResolvers = polyfill;
        console.log("[PDF Component] Worker polyfill injected");
      }
    } catch (e) {
      console.error("[PDF Component] Worker polyfill injection failed:", e);
    }
  }
})();

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import "./index.css";
import type { SearchDocument } from "@/types";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { debounce, throttle } from "lodash";
import { XMarkIcon } from "@heroicons/react/16/solid";
import eventBus from "@/utils/eventBus.ts";
import { useTranslation } from "@/locales";
import { HoverTip } from "../hoverTip";

// 配置 pdfjs worker 并确保 polyfill 在 worker 中可用
const originalWorkerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// 为 pdfjs worker 注入 polyfill
if (originalWorkerSrc && !originalWorkerSrc.includes("blob:")) {
  try {
    // 先获取原始 worker 代码
    fetch(originalWorkerSrc)
      .then((response) => response.text())
      .then((workerCode) => {
        // 创建包含 polyfill 和原始代码的新 worker
        const polyfillScript = `
          // Promise.withResolvers polyfill for PDF.js worker
          if (!Promise.withResolvers) {
            Promise.withResolvers = function() {
              let resolve, reject;
              const promise = new Promise((res, rej) => {
                resolve = res;
                reject = rej;
              });
              return { promise, resolve, reject };
            };
            console.log('[PDF Worker] Promise.withResolvers polyfill injected');
          }
          
          // 原始 worker 代码
          ${workerCode}
        `;

        const blob = new Blob([polyfillScript], {
          type: "application/javascript",
        });
        const workerUrl = URL.createObjectURL(blob);
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
        console.log("[PDF Component] Custom worker with polyfill created successfully");
      })
      .catch((error) => {
        console.error("[PDF Component] Failed to fetch original worker:", error);
        // 回退到原始配置
        pdfjs.GlobalWorkerOptions.workerSrc = originalWorkerSrc;
      });
  } catch (e) {
    console.error("[PDF Component] Failed to create custom worker:", e);
    // 回退到原始配置
    pdfjs.GlobalWorkerOptions.workerSrc = originalWorkerSrc;
  }
} else {
  // 如果已经是 blob URL，直接使用
  pdfjs.GlobalWorkerOptions.workerSrc = originalWorkerSrc;
}

interface PdfViewerProps {
  file: string;
  initialPage?: number;
  record?: SearchDocument;
  isClose?: boolean;
  onClose?: () => void;
  onPageChange?: (page: number) => void; // 监听组件页码改变
  onDocumentLoadSuccess?: (data: { numPages: number }) => void;
}

export const PdfView: React.FC<PdfViewerProps> = ({ file, initialPage = 1, record = null, isClose = false, onClose, onPageChange, onDocumentLoadSuccess }) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);
  const [pageInput, setPageInput] = useState<string>("1");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [isPagesLoading, setIsPagesLoading] = useState(true);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [hasScrolledToInitial, setHasScrolledToInitial] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const pageElementsRef = useRef<NodeListOf<Element> | null>(null);

  // 用于跟踪是否是程序触发的滚动
  const isProgrammaticScroll = useRef(false);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
  // 用于跟踪滚动方向
  const lastScrollPosition = useRef(0);
  // 用于存储当前滚动方向
  const scrollDirection = useRef<"up" | "down">("down");
  // 新增：追踪用户是否手动调整了缩放
  const [isManualZoom, setIsManualZoom] = useState(false);

  // 重置文档状态
  useEffect(() => {
    setNumPages(0);
    setPageInput(initialPage.toString());
    setCurrentPage(initialPage);
    setHasScrolledToInitial(false);
    setIsDocumentLoading(true);
    setIsPagesLoading(true);
    setError(null);
    setIsManualZoom(false);
    pdfDocRef.current = null;
  }, [file]);

  useEffect(() => {
    setTimeout(() => {
      scrollToPage(initialPage);
    }, 500);
  }, [initialPage]);
  // 文档加载成功
  const handleDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    console.log(initialPage);
    setNumPages(pdf.numPages);

    //总页数传给父组件
    if (onDocumentLoadSuccess) {
      onDocumentLoadSuccess({ numPages: pdf.numPages });
    }

    // setPageInput(initialPage.toString());
    // setCurrentPage(initialPage);
    pdfDocRef.current = pdf;
    setHasScrolledToInitial(false);
    setIsDocumentLoading(false);
    computeAutoScale();

    setTimeout(() => {
      scrollToPage(initialPage);
    }, 500);
  };

  useEffect(() => {
    const changePage = (page: number) => {
      scrollToPage(page);
    };
    eventBus.on("setPdfPage", changePage);

    return () => {
      eventBus.off("setPdfPage", changePage);
    };
  }, []);
  // 文档加载错误处理
  const handleDocumentLoadError = (error: Error) => {
    console.error("PDF加载失败: ", error);
    setError(`无法加载PDF文件: ${error.message}`);
    setIsDocumentLoading(false);
    setIsPagesLoading(false);
  };

  const handlePageRenderSuccess = () => {
    // 当所有页面都加载完成后更新状态
    const allPagesLoaded = Array.from({ length: numPages }).every((_, i) => {
      return pageRefs.current[i]?.querySelector(".my-4");
    });

    if (allPagesLoaded) {
      setIsPagesLoading(false);
    }
  };

  // 页面滚动到指定页

  const scrollToPage = (page: number) => {
    if (!scrollContainerRef.current) return;

    const index = page - 1;
    const pageElement = pageRefs.current[index];
    if (!pageElement) return;

    // 标记为程序滚动
    isProgrammaticScroll.current = true;

    // 记录触发的页面
    lastTriggeredPage.current = page;

    // 计算目标位置
    const scrollPosition = pageElement.offsetTop;

    scrollContainerRef.current.scrollTo({
      top: scrollPosition,
      behavior: "auto",
    });

    // 更新页码
    setCurrentPage(page);
    setPageInput(String(page));

    // 立即调用 onPageChange（因为这是用户主动操作）
    if (onPageChange) {
      onPageChange(page);
    }

    // 重置程序滚动标记
    setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 500);
  };

  // 初始滚动处理
  useEffect(() => {
    if (numPages > 0 && !hasScrolledToInitial && !isDocumentLoading) {
      scrollToPage(initialPage);
      setHasScrolledToInitial(true);
    }
  }, [numPages, initialPage, hasScrolledToInitial, isDocumentLoading]);

  // 核对页码
  const lastTriggeredPage = useRef<number | null>(null);
  const scrollEndTimer = useRef<NodeJS.Timeout | null>(null);
  // 使用原生方法监听滚动并更新页码
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || isDocumentLoading) return;

    // 获取所有类名为 "pdf-page-container" 的元素
    const updatePageElements = () => {
      pageElementsRef.current = container.querySelectorAll(".my-4");
    };

    // 初始获取页面元素
    updatePageElements();

    const handleScroll = () => {
      // 如果是程序触发的滚动，跳过处理
      if (isProgrammaticScroll.current) return;
      // 更新滚动方向
      const currentScrollPosition = container.scrollTop;

      if (currentScrollPosition > lastScrollPosition.current) {
        scrollDirection.current = "down";
      } else {
        scrollDirection.current = "up";
      }
      lastScrollPosition.current = currentScrollPosition;

      if (!pageElementsRef.current || pageElementsRef.current.length === 0) {
        updatePageElements();
        return;
      }

      const containerHeight = container.clientHeight;
      const scrollTop = container.scrollTop;
      const containerBottom = scrollTop + containerHeight;

      // 检查上一页是否完全可见
      if (currentPage > 1 && scrollDirection.current === "up") {
        const prevPageIndex = currentPage - 2;
        const prevPageElement = pageElementsRef.current[prevPageIndex];

        if (prevPageElement) {
          const prevPageTop = prevPageElement.offsetTop;
          const prevPageHeight = prevPageElement.clientHeight;
          const prevPageBottom = prevPageTop + prevPageHeight;

          // 如果上一页完全在视口内
          if (prevPageTop >= scrollTop && prevPageBottom <= containerBottom) {
            //核对功能页码
            const newPage = prevPageIndex + 1;
            // 更新当前页码但不立即触发回调
            setCurrentPage(newPage);
            setPageInput(String(newPage));
            return;
          }
        }
      }

      let currentVisiblePage = currentPage;
      let maxVisibleArea = 0;

      // 遍历所有页面元素，找到在视口中可见面积最大的页面
      pageElementsRef.current.forEach((element, index) => {
        const elementTop = element.offsetTop;
        const elementHeight = element.clientHeight;
        const elementBottom = elementTop + elementHeight;

        // 计算元素在视口中的可见区域
        const visibleTop = Math.max(scrollTop, elementTop);
        const visibleBottom = Math.min(containerBottom, elementBottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);

        // 如果这个元素在视口中的可见面积更大，则更新当前页
        if (visibleHeight > maxVisibleArea) {
          maxVisibleArea = visibleHeight;
          currentVisiblePage = index + 1;
        }
      });

      // 更新当前页码但不立即触发回调
      if (currentVisiblePage !== currentPage) {
        setCurrentPage(currentVisiblePage);
        setPageInput(String(currentVisiblePage));
      }

      // 清除之前的定时器
      if (scrollEndTimer.current) {
        clearTimeout(scrollEndTimer.current);
      }

      // 设置新的定时器，在滚动停止1000ms后触发页面变化回调
      scrollEndTimer.current = setTimeout(() => {
        // 检查当前页面是否已经触发过
        if (lastTriggeredPage.current !== currentPage) {
          lastTriggeredPage.current = currentPage;
          if (onPageChange) {
            onPageChange(currentPage);
          }
        }
      }, 500);
    };

    // 添加滚动事件监听
    container.addEventListener("scroll", handleScroll);

    // 清理函数
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollEndTimer.current) {
        clearTimeout(scrollEndTimer.current);
      }
    };
  }, [isDocumentLoading, currentPage, onPageChange]);
  // 页面输入提交
  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const target = parseInt(pageInput);
    if (!isNaN(target) && numPages > 0) {
      const safePage = Math.min(Math.max(target, 1), numPages);
      scrollToPage(safePage);
      // 更新最后触发的页面记录
      lastTriggeredPage.current = safePage;
    }
  };

  // 计算并设置自适应缩放比例
  const computeAutoScale = useCallback(async () => {
    if (isManualZoom || !pdfDocRef.current) return;

    try {
      const page = await pdfDocRef.current.getPage(1);
      const viewport = page.getViewport({ scale: 1 });
      const pageWidth = viewport.width;

      // 获取 PDF 页面外层容器（my-4 div）真正的容器宽度
      const containerEl = pageRefs.current[0]?.parentElement;
      if (!containerEl) return;

      const containerWidth = containerEl.clientWidth;

      console.log(containerWidth);
      // 设置边距（可调小一点让页面不贴边）
      const padding = 16;
      const availableWidth = containerWidth - padding;

      const newScale = Math.round(Math.max(0.5, Math.min(2.0, availableWidth / pageWidth) * 0.6));
      setScale(newScale);
    } catch (error) {
      console.error("自动缩放计算失败:", error);
    }
  }, [isManualZoom]);
  useEffect(() => {
    if (isManualZoom) return;

    const handleResize = debounce(() => {
      computeAutoScale();
    }, 300);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [computeAutoScale, isManualZoom]);

  // 缩放处理
  const zoomIn = () => {
    setIsManualZoom(true);
    setScale((prev) => Math.min(prev + 0.1, 2.0));
  };

  const zoomOut = () => {
    setIsManualZoom(true);
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  };

  const documentOptions = useMemo(
    () => ({
      cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
      cMapPacked: true,
      standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
      disableTextLayer: true,
      disableAutoFetch: true,
      disableStream: true,
    }),
    []
  );

  return (
    <div className="flex flex-col items-center pdf-viewer-container h-full">
      {/* 控制面板 */}
      <div className="pdf-top-bar top-0 bg-[var(--background)] z-10 p-2 border-b w-full justify-center flex-shrink-0">
        <div className="w-1/3">
          <HoverTip position={"bottom"} content={record ? record.fileName : ""}>
            <div className="pdf-file-name">{record ? record.fileName : ""}</div>
          </HoverTip>
        </div>
        <div className="flex flex-1 items-center">
          <Pagination className="mx-0">
            <PaginationContent>
              <PaginationItem>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scrollToPage(currentPage - 1)} disabled={currentPage <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </PaginationItem>

              <PaginationItem>
                <div className="flex items-center gap-1 px-2">
                  <form onSubmit={handlePageInputSubmit} className="flex items-center gap-1">
                    <input type="text" value={pageInput} onChange={(e) => setPageInput(e.target.value)} className="w-12 border rounded px-1 text-center" />
                    <span className="text-sm text-gray-600">/ {numPages}</span>
                  </form>
                </div>
              </PaginationItem>

              <PaginationItem>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => scrollToPage(currentPage + 1)} disabled={currentPage >= (numPages ?? 0)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
        {/* 缩放控制 */}
        <div className="zoom-percentage">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomOut} disabled={scale <= 0.5} title="缩小">
            <ZoomOut className="h-4 w-4" />
          </Button>
          <div className="scale-info">{Math.round(scale * 100)}%</div>

          <Button variant="outline" size="icon" className="h-8 w-8" onClick={zoomIn} disabled={scale >= 2.0} title="放大">
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
        {isClose && (
          <button className="ml-2 text-token-text-secondary hover:bg-[#e8e8e8] rounded-lg h-[32px] w-[32px] mr-1 flex items-center justify-center" onClick={onClose}>
            <XMarkIcon className="w-[22px] h-[22px]"></XMarkIcon>
          </button>
        )}
      </div>

      {/* 所有页面渲染（可滚动） */}
      <div className="w-full flex-1 overflow-y-auto" ref={scrollContainerRef}>
        {error ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="bg-red-100 rounded-full p-4 mb-4">
              <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2">无法加载PDF文件</h3>
            <p className="text-muted-foreground max-w-md text-center">{error}</p>
            <p className="text-muted-foreground text-sm mt-4">请确保文件是有效的PDF格式</p>
          </div>
        ) : file ? (
          <>
            {/* 文档加载骨架屏 */}
            {isDocumentLoading && (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={`doc-skeleton-${i}`} className="w-full h-[400px] rounded-md" />
                ))}
              </div>
            )}

            <Document
              key={file}
              file={file}
              options={documentOptions}
              onLoadSuccess={handleDocumentLoadSuccess}
              onLoadError={handleDocumentLoadError}
              loading={
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={`doc-skeleton-${i}`} className="w-full h-[400px] rounded-md" />
                  ))}
                </div>
              } // 禁用内置加载状态
            >
              {/* 页面加载骨架屏 */}
              {Array.from({ length: numPages }, (_, index) => (
                <div key={`page_${index + 1}`} ref={(el) => (pageRefs.current[index] = el)} className="my-4 ">
                  <Page
                    pageNumber={index + 1}
                    scale={scale}
                    width={Math.min(800, window.innerWidth - 80)}
                    renderAnnotationLayer={true}
                    renderTextLayer={true}
                    onRenderSuccess={handlePageRenderSuccess}
                    loading={<Skeleton key={`doc-skeleton-${index}`} className="w-full h-[400px] rounded-md" />}
                    className="!flex !justify-center shadow-md rounded border bg-white"
                  />
                </div>
              ))}
            </Document>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-full py-12">
            <div className="bg-gray-100 rounded-full p-4 mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-blue-500"></div>
            </div>
            <h3 className="text-xl font-semibold mb-2">{t("common.loadingInProgress")}</h3>
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={`doc-skeleton-${i}`} className="w-full h-[400px] rounded-md" />
              ))}
            </div>
          </div>
        )}

        {!isDocumentLoading && !isPagesLoading && numPages > 0 && currentPage >= numPages && <div className="text-center py-8 text-muted-foreground text-sm">已到达文档末尾</div>}
      </div>
    </div>
  );
};
